import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserGraduate,
  FaFileInvoiceDollar,
  FaTrash,
  FaPlus,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";
import { toast } from "react-toastify";
import AssignFeeForm from "../forms/AssignFeeForm";
import { server } from "../../server";
import useAuth from "../../useAuth";

export default function StudentFeeList() {
  const [items, setItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/student-fees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.data);
    } catch (error) {
      toast.error("Failed to load assigned student fees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchItems();
    // eslint-disable-next-line
  }, [token]);

  const handleDelete = async (id) => {
    if (window.confirm("Remove this assigned fee?")) {
      try {
        await axios.delete(`${server}/student-fees/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Fee Assignment Deleted");
        fetchItems();
      } catch (error) {
        toast.error("Error deleting");
      }
    }
  };

  return (
    <div className="p-8 bg-gray-50 dark:bg-slate-950 min-h-screen transition-colors duration-200">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
            <FaFileInvoiceDollar className="mr-3 text-emerald-600 dark:text-emerald-400" /> Student
            Fee Registry
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 ml-9 mt-1">
            Monitor billing and collect fee invoices for specific students.
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl flex items-center font-semibold hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 transition shadow-lg shadow-emerald-200 dark:shadow-none cursor-pointer"
          >
            <FaPlus className="mr-2" /> Assign Fee Bill
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden transition-colors">
        <table className="min-w-full divide-y divide-gray-100 dark:divide-slate-800">
          <thead className="bg-gray-50/50 dark:bg-slate-800/60">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider">
                Student
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider">
                Fee Details
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider">
                Month
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-50 dark:divide-slate-800 text-sm">
            {loading ? (
              <tr>
                <td
                  colSpan="6"
                  className="text-center py-8 text-gray-500 dark:text-slate-400 font-medium"
                >
                  Loading registry...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan="6"
                  className="text-center py-8 text-gray-400 dark:text-slate-500 font-medium italic"
                >
                  No fees assigned to students yet.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-emerald-50/30 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 p-2 rounded-full hidden sm:block">
                        <FaUserGraduate />
                      </div>
                      <div>
                        <div className="font-bold text-gray-800 dark:text-slate-200">
                          {item.student?.firstName} {item.student?.lastName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-slate-400 font-medium">
                          Class {item.student?.class} • Roll:{" "}
                          {item.student?.rollNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 dark:text-slate-300 font-medium">
                    <span className="font-bold text-gray-700 dark:text-slate-200">
                      {item.feeStructure?.title}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-bold text-emerald-600 dark:text-emerald-400">
                    {item.month && !isNaN(Date.parse(item.month))
                      ? new Date(item.month).toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })
                      : item.month}
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-black text-gray-800 dark:text-white">
                      ₹{item.totalAmount?.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-slate-400 font-bold uppercase mt-1">
                      Due: {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {item.status === "Paid" ? (
                      <span className="inline-flex items-center gap-1 bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-xs font-bold border border-green-200 dark:border-green-800/50">
                        <FaCheckCircle /> Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 px-3 py-1 rounded-full text-xs font-bold border border-orange-200 dark:border-orange-800/50">
                        <FaClock /> Pending
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2 bg-red-50 dark:bg-red-950/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors cursor-pointer"
                      title="Remove assigned fee"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-xs transition-opacity duration-300">
          <div className="w-full max-w-2xl px-4 relative z-[101]">
            <AssignFeeForm
              onClose={() => setIsModalOpen(false)}
              onSuccess={fetchItems}
            />
          </div>
        </div>
      )}
    </div>
  );
}
